import { useCallback, useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import { auth } from "../firebase";
import { ensureSession, trackSession, waitForSession } from "../lib/session";
import { materialUrl, parseMaterialPath, previewKind } from "../lib/materials";

/**
 * ①素材の中身を画面で確かめる。
 *
 * 配信は認可プロキシ（/m/{dishId}/**）を通す。Storage の署名付きURL・ダウンロードURLを
 * 使わないのは、**URL を持っているだけで誰でも開ける**形になり、素材（未編集の生の
 * 調理映像）が #13 の想定から外れて出回るため。プロキシなら毎回テナントと役割を見て、
 * 開いた事実が access_logs に残る。
 *
 * 認証は __session クッキーで運ぶ。<video> や <iframe> のリクエストには
 * Authorization ヘッダを付けられないので、クッキーが唯一の手段（設計 §4.1）。
 * **クッキーが張られる前に読みに行くと 401 で黙って失敗する**ので、
 * 開く前に待ち、失敗したら1度だけ張り直してやり直す。
 */
export function MaterialPreview({
  dishId,
  path,
  onClose,
}: {
  dishId: string;
  path: string;
  onClose: () => void;
}) {
  const { t, locale } = useI18n();
  const [ready, setReady] = useState(false);
  // 読み込みの世代。張り直した後に再読み込みさせるために使う
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    waitForSession().finally(() => {
      if (alive) setReady(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Esc で閉じる。動画を大きく出すので、閉じ方が分からなくならないように
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onError = useCallback(() => {
    // 期限切れ・別タブでのログアウトはここで復帰できる。2回目は素直に諦める
    if (attempt > 0 || !auth.currentUser) {
      setFailed(true);
      return;
    }
    trackSession(ensureSession(auth.currentUser))
      .then(() => setAttempt((n) => n + 1))
      .catch(() => setFailed(true));
  }, [attempt]);

  const info = parseMaterialPath(path);
  const base = materialUrl(dishId, path);
  // クエリを足して、張り直した後にキャッシュ済みの失敗を再利用させない
  const url = base && attempt > 0 ? `${base}?r=${attempt}` : base;
  const kind = previewKind(path);

  return (
    <div className="modal-back" role="dialog" aria-modal="true" aria-label={info.fileName} onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <strong className="modal-title">{info.fileName}</strong>
          <span className="spacer" />
          {url && (
            <a className="link" href={url} target="_blank" rel="noreferrer">
              {t("別タブで開く")}
            </a>
          )}
          <button className="link" onClick={onClose}>
            {t("閉じる")}
          </button>
        </div>

        {!base ? (
          <p className="error">{t("この素材の場所を解決できませんでした（保存先: {path}）。", { path })}</p>
        ) : failed ? (
          <div>
            <p className="error">{t("この素材を表示できませんでした。")}</p>
            <p className="muted small">
              {t("素材を開けるのは管理者と料理長のみです。権限がある場合は、ログインし直すと解消することがあります。")}
            </p>
          </div>
        ) : !ready ? (
          <p className="muted">{t("読み込み中…")}</p>
        ) : kind === "video" ? (
          // 元動画は 2GB 級。プロキシが Range に対応しているので頭出しできる
          <video className="modal-media" src={url} controls preload="metadata" onError={onError} />
        ) : kind === "image" ? (
          <img className="modal-media" src={url} alt={info.fileName} onError={onError} />
        ) : kind === "pdf" ? (
          <iframe className="modal-frame" src={url} title={info.fileName} onError={onError} />
        ) : (
          <p className="muted">
            {t("この形式は画面で表示できません。「別タブで開く」から内容を確認してください。")}
          </p>
        )}

        <p className="muted small">
          {t("保存先:")} <code>{path}</code>
          {info.uploadedAt && <> ／ {t("アップロード:")} {info.uploadedAt.toLocaleString(locale)}</>}
        </p>
      </div>
    </div>
  );
}

/**
 * 素材名のリンク。押すと中身を出す（履歴の詳細からも使う）。
 *
 * この品目の配下に無いパスはリンクにしない。押しても 403 になるだけで、
 * 「権限が無い」のか「参照が壊れている」のかが読む側に分からなくなる。
 */
export function MaterialLink({
  dishId,
  path,
  onOpen,
}: {
  dishId: string;
  path: string;
  onOpen: (path: string) => void;
}) {
  const info = parseMaterialPath(path);
  if (!materialUrl(dishId, path)) return <code title={path}>{info.fileName}</code>;
  return (
    <button type="button" className="link file" onClick={() => onOpen(path)} title={path}>
      {info.fileName}
    </button>
  );
}
